if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        
        if (this.elements.viewer) {
          this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        }
        
        if (this.elements.thumbnails) {
          this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
            mediaToSwitch
              .querySelector('button')
              .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
          });
        }
        
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();

        // Subscribe to variant changes to filter images
        this.variantChangeUnsubscriber = null;
      }

      connectedCallback() {
        // Check if filtering should be enabled based on product metafield
        // If disabled, ensure all images are visible immediately
        const displayAccordingToOptions = this.dataset.displayImagesAccordingToOptions;
        const shouldFilter = displayAccordingToOptions === 'true' || displayAccordingToOptions === '1';
        
        if (!shouldFilter) {
          // Show all images immediately when filtering is disabled
          requestAnimationFrame(() => {
            const viewerItems = this.elements.viewer ? Array.from(this.elements.viewer.querySelectorAll('li[data-media-id]')) : [];
            const thumbnailItems = this.elements.thumbnails ? Array.from(this.elements.thumbnails.querySelectorAll('li[data-target]')) : [];
            
            viewerItems.forEach((item) => {
              item.style.display = '';
              item.classList.remove('variant-filtered-hidden');
            });
            
            thumbnailItems.forEach((item) => {
              item.style.display = '';
              item.classList.remove('variant-filtered-hidden');
            });
            
            // Update slider counter
            const sliderCounter = this.querySelector('.slider-counter--total');
            if (sliderCounter && viewerItems.length > 0) {
              sliderCounter.textContent = viewerItems.length;
            }
          });
        }
        
        // Initialize variant filtering when element is connected to DOM
        this.initializeVariantFiltering();

        // Autoplay the initially active media (videos load via deferred-media)
        requestAnimationFrame(() => {
          const activeItem = this.elements.viewer?.querySelector('li.is-active[data-media-id]');
          if (activeItem) {
            this.playActiveMedia(activeItem);
          }
        });
      }

      initializeVariantFiltering() {
        // Wait for PUB_SUB_EVENTS and subscribe to be available
        if (typeof PUB_SUB_EVENTS === 'undefined' || typeof subscribe === 'undefined') {
          // Retry after a short delay if not available yet
          setTimeout(() => this.initializeVariantFiltering(), 100);
          return;
        }

        // Subscribe to variant changes
        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          if (!event.data || !event.data.variant) return;
          
          // Check if filtering should be enabled based on product metafield
          const displayAccordingToOptions = this.dataset.displayImagesAccordingToOptions;
          const shouldFilter = displayAccordingToOptions === 'true' || displayAccordingToOptions === '1';
          
          if (!shouldFilter) {
            // Show all images when variant changes if filtering is disabled
            const viewerItems = this.elements.viewer ? Array.from(this.elements.viewer.querySelectorAll('li[data-media-id]')) : [];
            const thumbnailItems = this.elements.thumbnails ? Array.from(this.elements.thumbnails.querySelectorAll('li[data-target]')) : [];
            
            viewerItems.forEach((item) => {
              item.style.display = '';
              item.classList.remove('variant-filtered-hidden');
            });
            
            thumbnailItems.forEach((item) => {
              item.style.display = '';
              item.classList.remove('variant-filtered-hidden');
            });
            
            // Update slider counter
            const sliderCounter = this.querySelector('.slider-counter--total');
            if (sliderCounter && viewerItems.length > 0) {
              sliderCounter.textContent = viewerItems.length;
            }
            return;
          }
          
          const variant = event.data.variant;
          this.filterMediaByVariant(variant);
        });

        // Filter on initial load based on current variant
        // Use requestAnimationFrame to ensure DOM is fully ready
        requestAnimationFrame(() => {
          // Check if filtering should be enabled based on product metafield
          const displayAccordingToOptions = this.dataset.displayImagesAccordingToOptions;
          const shouldFilter = displayAccordingToOptions === 'true' || displayAccordingToOptions === '1';
          
          if (!shouldFilter) {
            // Show all images - ensure all are visible
            const viewerItems = this.elements.viewer ? Array.from(this.elements.viewer.querySelectorAll('li[data-media-id]')) : [];
            const thumbnailItems = this.elements.thumbnails ? Array.from(this.elements.thumbnails.querySelectorAll('li[data-target]')) : [];
            
            viewerItems.forEach((item) => {
              item.style.display = '';
              item.classList.remove('variant-filtered-hidden');
            });
            
            thumbnailItems.forEach((item) => {
              item.style.display = '';
              item.classList.remove('variant-filtered-hidden');
            });
            
            // Update slider counter
            const sliderCounter = this.querySelector('.slider-counter--total');
            if (sliderCounter && viewerItems.length > 0) {
              sliderCounter.textContent = viewerItems.length;
            }
            
            return; // No filtering needed
          }

          let initialVariant = null;
          const productInfo = this.closest('product-info');
          const variantSelects = productInfo?.querySelector('variant-selects');

          // First, try to get variant from data attributes (simpler approach)
          const initialVariantId = this.dataset.initialVariantId;
          const initialVariantTitle = this.dataset.initialVariantTitle;
          if (initialVariantId && initialVariantTitle) {
            initialVariant = {
              id: parseInt(initialVariantId),
              title: initialVariantTitle,
              options: initialVariantTitle.split(' / ').map(opt => opt.trim())
            };
          }

          // Fallback: Try to get the current variant from variant-selects
          if (!initialVariant && variantSelects) {
            const selectedVariantElement = variantSelects.querySelector('[data-selected-variant]');
            if (selectedVariantElement && selectedVariantElement.textContent) {
              try {
                initialVariant = JSON.parse(selectedVariantElement.textContent);
              } catch (e) {
                console.warn('Failed to parse variant from variant-selects:', e);
              }
            }
          }

          // Last fallback: Try to get variant from URL parameter or form input
          if (!initialVariant) {
            const urlParams = new URLSearchParams(window.location.search);
            const variantId = urlParams.get('variant');
            const sectionId = this.closest('[data-section]')?.dataset.section || '';
            const variantIdInput = document.querySelector(`#product-form-${sectionId} input[name="id"]`);
            const variantIdToUse = variantId || variantIdInput?.value;
            
            if (variantIdToUse && productInfo) {
              // Try to get from productData if available
              if (productInfo.productData && productInfo.productData.variants) {
                initialVariant = productInfo.productData.variants.find(v => v.id.toString() === variantIdToUse);
              }
            }
          }

          // Apply filtering if we found a variant
          if (initialVariant) {
            this.filterMediaByVariant(initialVariant);
          }
        });
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          const shouldSkipMobileDeferredMediaScroll = !this.mql.matches && activeMedia.querySelector('.deferred-media');

          if ((!this.mql.matches || this.elements.thumbnails) && !shouldSkipMobileDeferredMediaScroll) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          if (shouldSkipMobileDeferredMediaScroll) return;

          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (!deferredMedia) {
          window.pauseAllMedia();
          return;
        }

        const loadMedia = () => {
          if (!activeItem.classList.contains('is-active')) return;
          if (typeof deferredMedia.loadContent === 'function') {
            deferredMedia.loadContent(false);
          }
        };

        if (customElements.get('deferred-media')) {
          loadMedia();
        } else {
          customElements.whenDefined('deferred-media').then(loadMedia);
        }
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }

      filterMediaByVariant(variant) {
        if (!variant) return;

        // Check if filtering should be enabled based on product metafield
        // If display_images_according_to_options is false, show all images regardless of variant
        const displayAccordingToOptions = this.dataset.displayImagesAccordingToOptions;
        const shouldFilter = displayAccordingToOptions === 'true' || displayAccordingToOptions === '1';
        
        if (!shouldFilter) {
          // Show all images - remove any hidden state
          const viewerItems = this.elements.viewer ? Array.from(this.elements.viewer.querySelectorAll('li[data-media-id]')) : [];
          const thumbnailItems = this.elements.thumbnails ? Array.from(this.elements.thumbnails.querySelectorAll('li[data-target]')) : [];
          
          viewerItems.forEach((item) => {
            item.style.display = '';
            item.classList.remove('variant-filtered-hidden');
          });
          
          thumbnailItems.forEach((item) => {
            item.style.display = '';
            item.classList.remove('variant-filtered-hidden');
          });
          
          // Update slider counter
          const sliderCounter = this.querySelector('.slider-counter--total');
          if (sliderCounter && viewerItems.length > 0) {
            sliderCounter.textContent = viewerItems.length;
          }
          
          return; // Exit early - no filtering needed
        }

        // Media is tagged through product image alt text in Liquid (`data-variant-identifier`).
        // The client expects media to match the full selected variant combination,
        // e.g. "Silver Pine / Blossom Pink / Navy Blue". Do not split by `/` here:
        // images tagged only as "Silver Pine" or "Navy Blue" must not show for that full variant.
        const variantTitle = variant.title ? variant.title.toLowerCase().trim() : '';
        
        // Get all media items in the viewer
        const viewerItems = this.elements.viewer ? Array.from(this.elements.viewer.querySelectorAll('li[data-media-id]')) : [];
        // Get all thumbnail items
        const thumbnailItems = this.elements.thumbnails ? Array.from(this.elements.thumbnails.querySelectorAll('li[data-target]')) : [];

        // Helper function to check if media has no variant identifier (no alt text)
        // Images without alt text should always be visible for all variants
        const hasNoVariantIdentifier = (mediaItem) => {
          if (!mediaItem) return false;
          
          const variantIdentifier = mediaItem.getAttribute('data-variant-identifier');
          
          // If attribute doesn't exist (null), show the image
          if (variantIdentifier === null) {
            return true;
          }
          
          // If attribute is an empty string, show the image
          // This happens when media.alt is blank in Liquid, which sets variant_identifier = ''
          // Liquid outputs: data-variant-identifier="", and getAttribute returns ""
          if (variantIdentifier === '') {
            return true;
          }
          
          // If attribute is a string with only whitespace, show the image
          if (typeof variantIdentifier === 'string' && variantIdentifier.trim() === '') {
            return true;
          }
          
          // If attribute has a non-empty value, it means the image has alt text with variant info
          // Return false so it goes through variant matching
          return false;
        };

        const identifierMatchesWithDescriptor = (identifier, value) => {
          if (!value) return false;

          // Allow merchant-friendly labels such as "Black Navy - Front" without falling
          // back to broad substring matching.
          return identifier.startsWith(`${value} -`) || identifier.startsWith(`${value}:`) || identifier.startsWith(`${value} |`);
        };

        // Match only exact full variant titles. Do not use `includes` or split `/` values:
        // overlapping names like "Black Navy" and "Navy" must remain separate.
        const mediaMatchesVariant = (mediaItem) => {
          // If no variant identifier (empty alt text), always show the media for all variants
          if (hasNoVariantIdentifier(mediaItem)) {
            return true;
          }

          const variantIdentifier = mediaItem.getAttribute('data-variant-identifier');
          const normalizedIdentifier = variantIdentifier.toLowerCase().trim();

          if (!variantTitle) return false;

          if (normalizedIdentifier === variantTitle || identifierMatchesWithDescriptor(normalizedIdentifier, variantTitle)) {
            return true;
          }

          return false;
        };

        // Filter viewer items
        let visibleCount = 0;
        let firstVisibleItem = null;
        let firstVariantMatchItem = null;
        let firstVariantVideoItem = null;
        let activeItemRemoved = false;

        viewerItems.forEach((item) => {
          const isActive = item.classList.contains('is-active');
          
          // First priority: Check if this item has no variant identifier (no alt text)
          // Images without alt text should ALWAYS be visible for all variants
          if (hasNoVariantIdentifier(item)) {
            item.style.display = '';
            item.classList.remove('variant-filtered-hidden');
            visibleCount++;
            if (!firstVisibleItem) {
              firstVisibleItem = item;
            }
            return; // Skip variant matching check for images without alt text
          }
          
          // Second priority: Check if this item matches the current variant
          const matches = mediaMatchesVariant(item);
          
          if (matches) {
            item.style.display = '';
            item.classList.remove('variant-filtered-hidden');
            visibleCount++;
            if (!firstVisibleItem) {
              firstVisibleItem = item;
            }
            if (!firstVariantMatchItem) {
              firstVariantMatchItem = item;
            }
            if (!firstVariantVideoItem && item.querySelector('.deferred-media')) {
              firstVariantVideoItem = item;
            }
          } else {
            item.style.display = 'none';
            item.classList.add('variant-filtered-hidden');
            if (isActive) {
              item.classList.remove('is-active');
              activeItemRemoved = true;
            }
          }
        });

        // Filter thumbnail items
        thumbnailItems.forEach((item) => {
          // First priority: Check if this item has no variant identifier (no alt text)
          // Images without alt text should ALWAYS be visible for all variants
          if (hasNoVariantIdentifier(item)) {
            item.style.display = '';
            item.classList.remove('variant-filtered-hidden');
            return; // Skip variant matching check for images without alt text
          }
          
          // Second priority: Check if this item matches the current variant
          const matches = mediaMatchesVariant(item);
          
          if (matches) {
            item.style.display = '';
            item.classList.remove('variant-filtered-hidden');
          } else {
            item.style.display = 'none';
            item.classList.add('variant-filtered-hidden');
          }
        });

        // Update slider counter if it exists
        const sliderCounter = this.querySelector('.slider-counter--total');
        if (sliderCounter && visibleCount > 0) {
          sliderCounter.textContent = visibleCount;
        }

        const activeItem = this.elements.viewer.querySelector('li.is-active:not(.variant-filtered-hidden)');
        const activeItemMatchesVariant = activeItem && !hasNoVariantIdentifier(activeItem) && mediaMatchesVariant(activeItem);
        const targetActiveItem = firstVariantVideoItem || firstVariantMatchItem || firstVisibleItem;
        const shouldSwitchActiveItem =
          targetActiveItem &&
          targetActiveItem !== activeItem &&
          (activeItemRemoved || !activeItem || !activeItemMatchesVariant || firstVariantVideoItem);

        if (shouldSwitchActiveItem) {
          const mediaId = targetActiveItem.getAttribute('data-media-id');
          if (mediaId) {
            this.setActiveMedia(mediaId, false);
          }
        }

        // Update slider if it exists
        if (this.elements.viewer && this.elements.viewer.slider) {
          this.elements.viewer.resetPages();
        }
        if (this.elements.thumbnails && this.elements.thumbnails.slider) {
          this.elements.thumbnails.resetPages();
        }
      }

      disconnectedCallback() {
        if (this.variantChangeUnsubscriber) {
          this.variantChangeUnsubscriber();
        }
      }
    }
  );
}
